import { defineComponent as _defineComponent } from 'vue'
import {computed, ref, watch} from 'vue';
import {useSimulationStore} from '@/components/calculator/services/SimulationStore';
import MultiformDictionaries, {currencyDictionary} from '@/modules/multiForm/shared/MultiformDictionaries';
import LoanPeriodSlider from '@/components/calculator/LoanPeriodSlider.vue';
import {debouncedAtInput} from '@/services/utils/BasicUtils';
import EnvironmentService from '@/env/EnvironmentService';
import {LoanPurposeWhat} from '@/models/simulation/LoanPurposeWhat';
import {IMultiformDictionary} from '@/modules/multiForm/shared/IMultiformDictionary';
import {LoanPurposeActivity} from '@/models/simulation/LoanPurposeActivity';
import {
  MORTGAGE_DEFAULT_LOAN_PERIOD_FOR_PURPOSE_LOAN_IN_YEARS,
  MORTGAGE_DEFAULT_LOAN_PERIOD_IN_YEARS
} from '@/models/Demand';
import HighLtvAlert from '@/components/calculator/HighLtvAlert.vue';
import {useCalculatorFormRules} from '@/components/calculator/useCalculatorFormRules';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import HomeStartProgramForm from '@/components/calculator/programs/Mortgage/HomeStartProgramForm.vue';
import OfferFilterCheckbox from '@/components/calculator/results/userInputPanel/OfferFilterCheckbox.vue';
import useOfferSorting, {FilterCategory, OfferFilter, OfferFilters} from '@/components/calculator/results/OfferSorting';

enum SavingsType {
  PERCENTAGE = 'PERCENTAGE',
  AMOUNT = 'AMOUNT',
}
export default /*#__PURE__*/_defineComponent({
  __name: 'MortgageSimulationFormStep1',
  props: {
    isFormValid: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  isFormValid: boolean,
}



;

const store = useSimulationStore();
const savingsAmount = ref<number>(store.userInput?.hypothecValue! - store.userInput?.loanAmount!);
const savingsPercentage = ref<number>(savingsAmount.value / store.userInput?.hypothecValue! * 100);
const savingsType = ref<SavingsType>(SavingsType.PERCENTAGE);
const {getVisibleFiltersByCategory,} = useOfferSorting();

const programOffersFilters = computed<OfferFilters[]>(
  () => getVisibleFiltersByCategory(FilterCategory.PROGRAM)
)

const {hypothecValueRules, loanAmountRules, savingsPercentageRules,} = useCalculatorFormRules();

const onHypothecValueChange = (value: number) => {
  savingsType.value === SavingsType.PERCENTAGE
    ? savingsAmount.value = Math.round(value * savingsPercentage.value / 100)
    : savingsPercentage.value = savingsAmount.value / value * 100;
  store.userInput.loanAmount! = Math.round(value - savingsAmount.value);
};

const onLoanAmountChange = (value: number) => {
  savingsAmount.value = Math.round(store.userInput?.hypothecValue! - value);
  savingsPercentage.value = savingsAmount.value / store.userInput?.hypothecValue! * 100;
};

const onSavingsAmountChange = (value: number) => {
  savingsType.value = SavingsType.AMOUNT;
  savingsPercentage.value = value / store.userInput?.hypothecValue! * 100;
  store.userInput.loanAmount! = Math.round(store.userInput?.hypothecValue! - value);
};

const onSavingsPercentageChange = (value: number) => {
  savingsType.value = SavingsType.PERCENTAGE;
  savingsAmount.value = Math.round(value / 100 * store.userInput?.hypothecValue!);
  store.userInput.loanAmount! = Math.round(store.userInput?.hypothecValue! - savingsAmount.value);
};
const onPurposeAsCollateralChange = (value: boolean) => {
  store.userInput!.purposeNotAsCollateral = !value;
  if (!value) {
    return;
  }
  handleLoanPurposeWhatSpecialCase(store.userInput!.loanPurpose.what!);
};

const handleLoanPurposeWhatSpecialCase = (value: LoanPurposeWhat) => {
  const isSpecialCase: boolean = [LoanPurposeWhat.HOUSE, LoanPurposeWhat.BUILDING_PLOT_AND_CONSTRUCTION,]
    .includes(value);
  store.userInput!.collateral = isSpecialCase
    ? LoanPurposeWhat.BUILDING_PLOT_AND_HOUSE : value;
};

const isPurposeAsCollateral = computed<boolean>({
  get: () => !store.userInput?.purposeNotAsCollateral,
  set: onPurposeAsCollateralChange,
});

const collaterals = computed<IMultiformDictionary<LoanPurposeWhat>>(() => {
  return MultiformDictionaries.fFCollaterals
    .filter(item => getLoanPurposeWhatItems(store.userInput!.loanPurpose.activity)
      .includes(item.type));
});
const isOtherCollateralVisible = computed<boolean>(() => [
  LoanPurposeActivity.BUILDING,
  LoanPurposeActivity.BUYING,
  LoanPurposeActivity.RENOVATING,
  LoanPurposeActivity.BUYING_AND_RENOVATING,
  LoanPurposeActivity.REFINANCING_REAL_ESTATE_LOAN,
  LoanPurposeActivity.REFINANCING_NON_REAL_ESTATE_LOAN,
  LoanPurposeActivity.REFINANCING_RESIDENTIAL_REAL_ESTATE,
  LoanPurposeActivity.REFINANCING_NONRESIDENTIAL_REAL_ESTATE,
].includes(store.userInput?.loanPurpose.activity!));

watch(() => store.userInput!.loanPurpose.activity, () => {
  selectFirstAvailableLoanPurposeWhat();
  if (!store.deal) {
    setDefaultLoanPeriod();
  }
});

watch(() => isOtherCollateralVisible?.value, (value, oldValue) => {
  if (!oldValue && value && !store.userInput?.purposeNotAsCollateral) {
    store.userInput!.collateral = store.userInput!.loanPurpose.what!;
  }
});
const selectFirstAvailableLoanPurposeWhat = () => {
  const isSelectedLoanPurposeWhatInCollaterals = collaterals.value
    .some(item => item.type === store.userInput?.loanPurpose.what);
  if (!isSelectedLoanPurposeWhatInCollaterals) {
    store.userInput!.collateral = collaterals.value[0].type;
    store.userInput!.loanPurpose.what = isOtherCollateralVisible.value ? collaterals.value[0].type : null;
  }
};

const setDefaultLoanPeriod = () => {
  const loanPeriod = store.userInput?.loanPurpose.activity === LoanPurposeActivity.LOAN
    ? MORTGAGE_DEFAULT_LOAN_PERIOD_FOR_PURPOSE_LOAN_IN_YEARS : MORTGAGE_DEFAULT_LOAN_PERIOD_IN_YEARS;
  store.userInput!.loanPeriod = EnvironmentService.Environment.isBackendSimV2 ? loanPeriod * 12 : loanPeriod;
  store.userInput!.loanPeriodInMonths = loanPeriod * 12;
};

// To będzie do wykasowania po wprowadzeniu simV2
const onLoanPeriodInMonthsChange = debouncedAtInput(($event: number) => {
  if (EnvironmentService.Environment.isBackendSimV2) {
    store.userInput!.loanPeriod = $event;
  } else {
    store.userInput!.loanPeriod = Math.round($event / 12);
    store.userInput!.loanPeriodInMonths = $event;
  }
});
const onSliderInput = debouncedAtInput(($event: number) => {
  if (!EnvironmentService.Environment.isBackendSimV2) {
    store.userInput!.loanPeriodInMonths = $event ? $event * 12 : null;
  }
});

const getLoanPurposeWhatItems = (loanPurpose: LoanPurposeActivity) => {
  switch (loanPurpose) {
  case LoanPurposeActivity.BUYING:
    return [LoanPurposeWhat.APARTMENT, LoanPurposeWhat.HOUSE,
      LoanPurposeWhat.OFFICE, LoanPurposeWhat.HOLIDAY_HOME, LoanPurposeWhat.BUILDING_PLOT,
      LoanPurposeWhat.RECREATION_PLOT, LoanPurposeWhat.AGRICULTURAL_PLOT,];
  case LoanPurposeActivity.ADAPTATION:
  case LoanPurposeActivity.LOAN:
  case LoanPurposeActivity.HOME_DEBT_CONSOLIDATION_LOAN:
  case LoanPurposeActivity.CONSUMER_DEBT_CONSOLIDATION_LOAN:
    return [
      LoanPurposeWhat.APARTMENT, LoanPurposeWhat.OFFICE,
      LoanPurposeWhat.HOLIDAY_HOME, LoanPurposeWhat.RECREATION_PLOT,
      LoanPurposeWhat.BUILDING_PLOT, LoanPurposeWhat.BUILDING_PLOT_AND_HOUSE,];
  case LoanPurposeActivity.BUILDING:
    return [LoanPurposeWhat.HOUSE, LoanPurposeWhat.OFFICE, LoanPurposeWhat.HOLIDAY_HOME,];
  case LoanPurposeActivity.BUYING_PLOT_AND_BUILDING:
    return [LoanPurposeWhat.BUILDING_PLOT_AND_HOUSE, LoanPurposeWhat.HOLIDAY_HOME,];
  default:
    return [LoanPurposeWhat.APARTMENT, LoanPurposeWhat.HOUSE,
      LoanPurposeWhat.OFFICE, LoanPurposeWhat.HOLIDAY_HOME,];
  }
};

const isLoanPurposeWhatVisible = computed<boolean>(() => ![
  LoanPurposeActivity.ADAPTATION,
  LoanPurposeActivity.LOAN,
  LoanPurposeActivity.BUYING_PLOT_AND_BUILDING,
  LoanPurposeActivity.CONSUMER_DEBT_CONSOLIDATION_LOAN,
  LoanPurposeActivity.HOME_DEBT_CONSOLIDATION_LOAN,
].includes(store.userInput!.loanPurpose.activity));

const onLoanPurposeWhatChange = (value: LoanPurposeWhat) => {
  store.userInput!.collateral = value;
  handleLoanPurposeWhatSpecialCase(value);
};


return { __sfc: true,props, SavingsType, store, savingsAmount, savingsPercentage, savingsType, getVisibleFiltersByCategory, programOffersFilters, hypothecValueRules, loanAmountRules, savingsPercentageRules, onHypothecValueChange, onLoanAmountChange, onSavingsAmountChange, onSavingsPercentageChange, onPurposeAsCollateralChange, handleLoanPurposeWhatSpecialCase, isPurposeAsCollateral, collaterals, isOtherCollateralVisible, selectFirstAvailableLoanPurposeWhat, setDefaultLoanPeriod, onLoanPeriodInMonthsChange, onSliderInput, getLoanPurposeWhatItems, isLoanPurposeWhatVisible, onLoanPurposeWhatChange, MultiformDictionaries, currencyDictionary, LoanPeriodSlider, HighLtvAlert, I18NGetter, HomeStartProgramForm, OfferFilterCheckbox }
}

})